import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, PencilLine, PlusCircle, X } from 'lucide-react'

const VARIANT_META = {
  default: { icon: <PencilLine size={16} />, className: 'is-default' },
  add: { icon: <PlusCircle size={16} />, className: 'is-add' },
  edit: { icon: <PencilLine size={16} />, className: 'is-edit' },
  delete: { icon: <AlertTriangle size={16} />, className: 'is-delete' },
}

export function Modal({ open, title, subtitle, children, onClose, footer, variant = 'default' }) {
  useEffect(() => {
    if (!open) return undefined
    document.body.classList.add('modal-open')
    return () => document.body.classList.remove('modal-open')
  }, [open])

  if (!open) return null

  const inferredVariant = (() => {
    if (variant !== 'default') return variant
    const normalized = String(title || '').toLowerCase()
    if (normalized.includes('delete')) return 'delete'
    if (normalized.includes('add') || normalized.includes('create') || normalized.includes('compose')) return 'add'
    if (normalized.includes('edit') || normalized.includes('change') || normalized.includes('update')) return 'edit'
    return 'default'
  })()
  const meta = VARIANT_META[inferredVariant] || VARIANT_META.default

  return createPortal((
    <div className="modal-backdrop" onClick={onClose}>
      <div className={`modal-card ${meta.className}`} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="modal-head">
          <div className="modal-head-left">
            <span className="modal-head-icon">{meta.icon}</span>
            <div>
            <h3>{title}</h3>
            {subtitle ? <p>{subtitle}</p> : null}
            </div>
          </div>
          <button className="btn btn-ghost btn-icon modal-close" onClick={onClose} aria-label="Close modal">
            <X size={16} />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer ? <div className="modal-footer">{footer}</div> : null}
      </div>
      <style>{`
        body.modal-open {
          overflow: hidden;
        }
        .modal-backdrop {
          position: fixed;
          inset: 0;
          z-index: 300;
          background: rgba(2, 6, 23, .5);
          backdrop-filter: blur(10px) saturate(110%);
          -webkit-backdrop-filter: blur(10px) saturate(110%);
          display: grid;
          place-items: center;
          padding: var(--space-md);
          animation: fade-in .18s ease;
        }
        .modal-card {
          width: min(620px, 100%);
          border-radius: var(--radius-lg);
          border: 1px solid var(--border);
          background: var(--bg-surface);
          box-shadow: var(--shadow-lg), 0 20px 48px rgba(2, 6, 23, .25);
          animation: lift-in .2s ease;
          overflow: hidden;
        }
        .modal-card.is-add { border-color: rgba(16,185,129,.35); }
        .modal-card.is-edit { border-color: rgba(79,70,229,.35); }
        .modal-card.is-delete { border-color: rgba(239,68,68,.35); }
        .modal-card.is-default { border-color: var(--border-active); }
        .modal-card::before {
          content: '';
          display: block;
          height: 2px;
          background: linear-gradient(90deg, var(--accent), var(--accent-2), transparent);
        }
        .modal-card.is-delete::before {
          background: linear-gradient(90deg, rgba(239,68,68,.95), rgba(249,115,22,.85), transparent);
        }
        .modal-head {
          padding: var(--space-md) var(--space-lg);
          border-bottom: 1px solid var(--border);
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: var(--space-sm);
          background: linear-gradient(180deg, rgba(255,255,255,.015), transparent);
        }
        .modal-head-left {
          display: flex;
          align-items: flex-start;
          gap: 10px;
        }
        .modal-head-icon {
          width: 30px;
          height: 30px;
          border-radius: 8px;
          display: grid;
          place-items: center;
          color: var(--accent-hover);
          background: var(--bg-raised);
          border: 1px solid var(--border);
          flex-shrink: 0;
        }
        .modal-card.is-add .modal-head-icon { color: var(--color-green); border-color: rgba(16,185,129,.3); background: rgba(16,185,129,.08); }
        .modal-card.is-delete .modal-head-icon { color: var(--color-red); border-color: rgba(239,68,68,.3); background: rgba(239,68,68,.08); }
        .modal-close {
          color: var(--text3);
          margin-top: 1px;
        }
        .modal-close:hover {
          color: var(--text1);
        }
        .modal-head h3 { font-size: var(--fs-lg); color: var(--text1); }
        .modal-head p { margin-top: 4px; color: var(--text3); font-size: var(--fs-sm); }
        .modal-body {
          padding: var(--space-lg);
          max-height: min(64vh, 680px);
          overflow-y: auto;
        }
        .modal-footer {
          padding: var(--space-md) var(--space-lg);
          border-top: 1px solid var(--border);
          display: flex;
          justify-content: flex-end;
          gap: var(--space-sm);
          background: linear-gradient(0deg, rgba(255,255,255,.012), transparent);
        }
        .modal-footer .btn {
          min-width: 96px;
        }
        @keyframes lift-in { from { opacity: .6; transform: translateY(10px) scale(.99);} to { opacity: 1; transform: translateY(0) scale(1);} }
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        @media (max-width: 640px) {
          .modal-card { width: min(100%, 520px); }
          .modal-body { padding: var(--space-md); }
          .modal-head, .modal-footer { padding: var(--space-sm) var(--space-md); }
          .modal-footer { flex-wrap: wrap; }
          .modal-footer .btn { flex: 1; min-width: 120px; }
        }
      `}</style>
    </div>
  ), document.body)
}

export function ConfirmModal({ open, title, message, onClose, onConfirm }) {
  return (
    <Modal
      open={open}
      title={title}
      subtitle={message}
      onClose={onClose}
      variant="delete"
      footer={(
        <>
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-danger" onClick={onConfirm}>Delete</button>
        </>
      )}
    >
      <div style={{ color: 'var(--text2)', fontSize: 'var(--fs-sm)' }}>
        This action cannot be undone.
      </div>
    </Modal>
  )
}
